import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect, useCallback } from "react";
import styles from "./NewHealthcareForm.module.css";
import { CpnwContainer } from "hooks/useCpnw";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { Loading, MembershipDuesTable, AdditionalInstitutionForm, InstitutionForm, InvoicingForm, SubmitButton, PackageList } from "@cpnw/ui";

// Component for handling a single healthcare institution section in the form
const HealthcareInstutionSection = forwardRef((props, ref) => {
  const { data, onChange, onRemove } = props;

  // Refs for scrolling into view on validation error
  const institutionSectionRef = useRef();
  const institutionInfoFormRef = useRef();

  const hcPackageSectionRef = useRef();
  const hcPackageFormRef = useRef(); // (Note: currently unused)

  const invoicingSectionRef = useRef();
  const invoicingFormRef = useRef();

  // Error state for package validation
  const [hcPackageError, setHCPackageError] = useState("");

  // Expose validation function to parent via ref
  useImperativeHandle(ref, () => ({
    validate: () => {
      const institutionInfoFormResult = institutionInfoFormRef.current?.validate?.();

      const hcPackageFormResult = data.healthcarePackages.some(pkg => pkg.isSelected);

      setHCPackageError(hcPackageFormResult ? "" : "Please select a package");

      const invoicingFormResult = invoicingFormRef.current?.validate?.();

      // Scroll to first invalid section
      requestAnimationFrame(() => {
        if (institutionInfoFormResult === false && institutionSectionRef.current) {
          institutionSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (hcPackageFormResult === false && hcPackageSectionRef.current) {
          hcPackageSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (invoicingFormResult === false && invoicingSectionRef.current) {
          invoicingSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });

      return institutionInfoFormResult & hcPackageFormResult && invoicingFormResult;
    }
  }));

  // Handle updates to nested form data
  const handleChange = useCallback((field, value) => {
    if (onChange) {
      onChange({
        ...data,
        [field]: value
      });
    }
  }, [data, onChange]);

  return (
    <div>
      {/* Institution Info Section */}
      <div className={styles.sectionContainer} ref={institutionSectionRef}>
        <h3 className={styles.subHeader}>Healthcare Institution</h3>
        <p>
          Please complete the following information for your healthcare
          institution below. This information will be used for official
          correspondence from CPNW. The primary contact should be the individual
          responsible for oversight of the program accounts you are requesting.
        </p>
        <InstitutionForm
          ref={institutionInfoFormRef}
          institutionFormData={data.institutionInfo}
          onChange={(updated) => {
            handleChange("institutionInfo", updated);
          }}
        />
      </div>

      {/* Healthcare Package Selection Section */}
      <div className={styles.sectionContainer} ref={hcPackageSectionRef}>
        <h3 className={styles.subHeader}>Healthcare Package Breakdown</h3>
        <p className={styles.requiredMessage}>**Required</p>

        {hcPackageError && (
          <p className={styles.errorMessage}>{hcPackageError}</p>
        )}
        <PackageList
          allowDeselect
          packages={data.healthcarePackages}
          onSelect={(selectedItem) => {
            const updated = data.healthcarePackages.map((pkg) => ({
              ...pkg,
              isSelected: selectedItem ? pkg.id === selectedItem.id : false,
            }));
            handleChange("healthcarePackages", updated);
          }}
          isDisabled={false}
        />
      </div>

      {/* Membership Dues Info Section */}
      <div className={styles.sectionContainer}>
        <h3 className={styles.subHeader}>Membership Dues</h3>
        <p className={styles.membershipDues}>
          The Healthcare Membership fee structure ensures continued support for
          enhanced services. Healthcare Membership Invoicing is generated within
          the following tier system, based on the total clinical placements
          within the academic year.
        </p>
        <div className={styles.membershipDuesTable}>
          <MembershipDuesTable />
        </div>
      </div>

      {/* Invoicing Info Section */}
      <div className={styles.sectionContainer} ref={invoicingSectionRef}>
        <h3 className={styles.subHeader}>Invoicing Information</h3>
        <p>Healthcare members can expect to be invoiced by January 31st of the Calendar year.</p>
        <InvoicingForm
          ref={invoicingFormRef}
          healthcareInvoicingData={
            data.invoicingInfo}
          onChange={(updated) => {
            handleChange("invoicingInfo", updated);
          }}
        />
      </div>

      {/* Optional Remove Button */}
      {onRemove && (
        <div className={styles.removeButtonContainer}>
          <button
            type="button"
            className={styles.removeButton}
            onClick={onRemove}>
            <FontAwesomeIcon icon={faTrash} className={styles.trashIcon} /> Remove
          </button>
        </div>
      )}
    </div>
  )
});

// Component for rendering an additional healthcare facility section
const AdditionalHCFacilitySection = forwardRef((props, ref) => {
  const { data, onChange, onRemove, healthcarePackages } = props;
  // Refs for section container and form
  const hcFacilitySectionRef = useRef();
  const hcFacilityInfoFormRef = useRef();

  // Expose a placeholder validate function to parent
  useImperativeHandle(ref, () => ({
    validate: () => {
      return true;
    }
  }));

  // Handle updates to form fields and propagate to parent
  const handleChange = useCallback((field, value) => {
    if (onChange) {
      onChange({
        ...data,
        [field]: value
      });
    }
  }, [data, onChange]);

  return (
    <div>
      <div className={styles.sectionContainer} ref={hcFacilitySectionRef}>
        {/* Section header and optional remove button */}
        <div className={styles.headerRow}>
          <h3 className={styles.subHeader}>Healthcare Facility Information</h3>
          {onRemove && (
            <div className={styles.removeButtonContainer}>
              <button
                type="button"
                className={styles.removeButton}
                onClick={onRemove}>
                <FontAwesomeIcon icon={faTrash} className={styles.trashIcon} /> Remove
              </button>
            </div>
          )}
        </div>

        {/* Facility info form component */}
        <AdditionalInstitutionForm
          ref={hcFacilityInfoFormRef}
          data={data.hcFacilityInfo}
          healthcarePackages={healthcarePackages}
          onChange={(updated) => {
            handleChange("hcFacilityInfo", updated);
          }}
        />
      </div>
    </div>
  )
});


// Component for rendering the main healthcare facility setup section
const MainHCFacility = forwardRef((props, ref) => {
  const { data, onChange } = props;

  // Refs for DOM access and child component method calls
  const institutionSectionRef = useRef();
  const institutionRef = useRef();

  const hcFacilitySectionRef = useRef(); // Reserved for future use
  const hcFacilitySeRef = useRef(); // Reserved for future use

  // Provide a validate function to parent components
  useImperativeHandle(ref, () => ({
    validate: () => {
      const institutionResult = institutionRef.current?.validate?.();
      const hcFacilityResult = hcFacilitySeRef.current?.validate?.();
      return institutionResult && hcFacilityResult;
    },
  }));

  // Handle data updates and pass them up to parent
  const handleChange = useCallback((propName, propData) => {
    const updated = { ...data, [propName]: propData };
    onChange?.(updated);
  }, [onChange]);

  return (
    <div>
      {/* Main healthcare institution form section */}
      <div className={styles.institutionComponent} ref={institutionSectionRef}>
        <HealthcareInstutionSection
          ref={institutionRef}
          data={data.mainHealthcare}
          onChange={(updated) =>
            handleChange("mainHealthcare", updated)
          }
        />
      </div>
    </div>
  );
});


// -------------------- NewHealthcareForm --------------------
export default function NewHealthcareForm() {
  // ----------------- Container data & package state setup -----------------
  const { refetchServicePackages, healthcareRegisterPost } = CpnwContainer.useContainer();

  const [healthcarePackages, setHealthcarePackages] = useState([]);

  const [postLoading, setPostLoading] = useState(false); // Tracks loading state during submission

  // ----------------- Default healthcare facility form template -----------------
  const hcFacilityFormData = {
    hcFacilityInfo: {
      name: "",
      address: "",
      servicePackage: "",
      billingInfo: "",
      newBillingInfo: "",
      additionalInfo: "",
    },
  };

  // ----------------- Form state -----------------
  const [formData, setFormData] = useState({
    mainHealthcare: {
      institutionInfo: {
        name: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        primaryContactName: "",
        primaryContactEmail: "",
        primaryContactPhone: "",
      },
      healthcarePackages: [],
      invoicingInfo: {
        invoicingEmail: "",
        copyEmail: "",
        additionalInfo: "",
      },
    },
    hcFacility: {
      ...hcFacilityFormData,
    },
    additionalHCFacilities: [],
  });


  // ----------------- Form references -----------------
  const primaryHCFacilityRef = useRef(); // Ref to primary healthcare facility section
  const hcFacilityRefs = useRef([]); // Refs for additional healthcare facility

  // Sync refs when additionalHCFacilities changes
  const syncRefsWithHCFacilities = (facilities) => {
    hcFacilityRefs.current = facilities.map((_, idx) => hcFacilityRefs.current[idx] || React.createRef());
  };

  // Sync on mount and when healthcare facilities added/removed
  useEffect(() => {
    syncRefsWithHCFacilities(formData.additionalHCFacilities);
  }, [formData.additionalHCFacilities]);


  /**
   * Extracts and transforms packages from a list by a specific type.
   * Also attempts to parse the description field if it's a JSON string.
   *
   * @param {Array} data - The array of package objects from the backend.
   * @param {string} type - The type of package to filter by.
   * @returns {Array} An array of package objects with consistent structure.
   */
  function getPackagesByType(data, type) {
    if (!Array.isArray(data)) return []; // Guard clause for invalid data

    return data
      .filter((pkg) => pkg.type === type) // Filter packages by desired type
      .map((pkg) => {
        let listItems = [];
        let description = "";

        try {
          // Attempt to parse the description as JSON (may include listItems)
          const parsed = JSON.parse(pkg.description);
          listItems = parsed.listItems || [];
        } catch (err) {
          // Fallback if description is plain text or invalid JSON
          listItems = [];
          description = pkg.description;
          // console.error(`Failed to parse description for package ID ${pkg.id}:`, err);
        }

        // Return a normalized package object
        return {
          id: pkg.id,
          name: pkg.name,
          description: description,
          isDefault: pkg.isDefault,
          isSelected: pkg.isSelected,
          options: pkg.options, // Option groups, if applicable
          listItems: listItems.map((item) => ({
            text: item.text,
            nestedItems: item.nestedItems || [], // Support for hierarchical display
          })),
        };
      });
  }

  // ----------------- Fetch and set service packages on mount -----------------
  useEffect(() => {
    refetchServicePackages().then((res) => {
      if (res?.data) {
        // Extract and store packages by type
        const healthcarePackages = getPackagesByType(res.data, "HEALTHCARE_PACKAGE");

        // Save in state
        setHealthcarePackages(healthcarePackages);

        // Update formData with loaded packages
        setFormData((prev) => ({
          ...prev,
          mainHealthcare: {
            ...prev.mainHealthcare,
            healthcarePackages,
          },
        }));
      }
    });
  }, []);

  // ----------------- Validation logic -----------------
  const validate = async () => {
    // Validate main form section
    const isPrimaryValid = await Promise.resolve(primaryHCFacilityRef.current?.validate?.()).then(res => res ?? true);

    // Validate all additional healthcare facilities
    const areHCFacilitiesValid = await Promise.all(
      hcFacilityRefs.current.map((ref) =>
        Promise.resolve(ref?.current?.validate?.()).then((res) => res ?? true)
      )
    );

    // Final validation result
    const allValid = isPrimaryValid && areHCFacilitiesValid.every(Boolean);
    return allValid;
  };

  // ----------------- Submission logic -----------------
  const handleSubmit = async () => {
    const isValid = await validate(); // Block submission if invalid
    if (!isValid) return;

    setPostLoading(true); // Show loading spinner
    console.log(formData);

    // Helper function to get selected packages
    const extractSelectedPackages = (packages) =>
      packages.flatMap(pkgArray =>
        pkgArray.filter(pkg => pkg.isSelected).map(({ id, name }) => ({ id, name }))
      );

    const findPackageByName = (packages, targetName) => {
      return packages.find(pkg => pkg.name === targetName);
    }

    try {

      const hcPayload = {
        // Healthcare Institution
        hcFacilityName: formData.mainHealthcare.institutionInfo.name,
        hcFacilityMailingAddress: formData.mainHealthcare.institutionInfo.address,
        city: formData.mainHealthcare.institutionInfo.city,
        state: formData.mainHealthcare.institutionInfo.state,
        zipCode: formData.mainHealthcare.institutionInfo.zip,
        primaryContactName: formData.mainHealthcare.institutionInfo.primaryContactName,
        primaryContactEmail: formData.mainHealthcare.institutionInfo.primaryContactEmail,
        primaryContactPhone: formData.mainHealthcare.institutionInfo.primaryContactPhone,

        // Handle all package selections including nested options
        servicePackages: [
          ...extractSelectedPackages([
            formData.mainHealthcare.healthcarePackages,
          ]),
        ],

        // Invoicing Information
        invoiceEmail: formData.mainHealthcare.invoicingInfo.invoicingEmail,
        invoiceCcEmail: formData.mainHealthcare.invoicingInfo.copyEmail,
        additionalInfo: formData.mainHealthcare.invoicingInfo.additionalInfo,

        // Repeat for Additional Healthcare Facilities
        additionalHCFacilities: formData.additionalHCFacilities.map((hcFacility) => ({
          hcFacilityName: hcFacility.hcFacilityInfo.name,
          hcFacilityMailingAddress: hcFacility.hcFacilityInfo.address,
          billingInfo: hcFacility.hcFacilityInfo.billingInfo,
          additionalInfo: hcFacility.hcFacilityInfo.additionalInfo,
          servicePackages: [findPackageByName(healthcarePackages, hcFacility.hcFacilityInfo.servicePackage)]
        })),
      };

      const result = await healthcareRegisterPost(hcPayload);

      if (result.success) {
        console.log("response", result);
        alert("Registration completed successfully!");
        window.location.reload(); // Optional reset
      } else {
        throw new Error("Submission failed");
      }

    } catch (error) {
      console.log(error);
      alert("Something went wrong while submitting the form. Please try again.");
    } finally {
      setPostLoading(false); // End loading state
    }
  };

  // ----------------- Event handlers -----------------

  // Update entire form object
  const handleChange = (updated) => {
    setFormData(updated);
  };

  // Update single healcare facility entry
  const handleAdditionalHCFacilityChange = (index, updatedHCFacility) => {
    setFormData(prev => {
      const updatedHCFacilities = [...prev.additionalHCFacilities];
      updatedHCFacilities[index] = updatedHCFacility;
      return { ...prev, additionalHCFacilities: updatedHCFacilities };
    });
  };

  // Remove a healcare facility entry
  const handleRemoveAdditionalHCFacility = (index) => {
    setFormData(prev => {
      const updatedHCFacilities = [...prev.additionalHCFacilities];
      updatedHCFacilities.splice(index, 1);
      return { ...prev, additionalHCFacilities: updatedHCFacilities };
    });
  };

  // Add another HC Facility section
  const handleAddMore = () => {
    const newHealcareFacility = {
      ...structuredClone(hcFacilityFormData),
      id: Date.now(), // Unique ID for rendering
    };

    setFormData(prev => ({
      ...prev,
      additionalHCFacilities: [...prev.additionalHCFacilities, newHealcareFacility],
    }));

    hcFacilityRefs.current.push(React.createRef()); // Sync with new form
  };

  // ----------------- Render -----------------
  return (
    <div className={styles.HealthcareFormContainer}>
      <div className={styles.sectionContainer}>
        {/* Intro section */}
        <h1 className={styles.welcomeHeader}>Welcome Healthcare Partners</h1>

        <p>
          To complete this registration form, you will need to provide details
          about your facility or the multiple facilities you represent. Ensuring
          that all individual facilities within your parent institution are
          listed will allow us to set up your CPNW account accurately. This
          ensures that student assignments to each location are correctly
          managed and that site-specific requirements are appropriately applied.
        </p>

        <p>
          <span className={styles.exception}>Exception:</span> Ambulatory or
          clinic settings are bundled together to streamline system
          functionality, so you do not need to list clinic/ambulatory setting
          locations individually.
        </p>
      </div>

      {/* PrimaryHCFacility block */}
      <MainHCFacility
        ref={primaryHCFacilityRef}
        data={formData}
        onChange={(updated) => handleChange(updated)}
      />

      {/* Render additional HC Facility components */}
      {formData.additionalHCFacilities.map((facility, idx) => (
        <AdditionalHCFacilitySection
          key={facility.id}
          ref={hcFacilityRefs.current[idx]}
          data={facility}
          onChange={(updated) => handleAdditionalHCFacilityChange(idx, updated)}
          onRemove={() => handleRemoveAdditionalHCFacility(idx)}
          healthcarePackages={healthcarePackages}
        />
      ))}

      {/* Button to add another HC Facility */}
      <div className={styles.addButtonContainer}>
        <button
          type="button"
          className={styles.addButton}
          onClick={(e) => { e.preventDefault(); handleAddMore(); }}
        >
          <FontAwesomeIcon icon={faPlus} className={styles.icon} /> Add Additional Healthcare Facility
        </button>
      </div>

      {/* Submit button */}
      <div className={styles.buttonContainer}>
        <SubmitButton onClick={handleSubmit} />
      </div>

      {/* Loading indicator on form submission */}
      {postLoading && (
        <div className={styles.overlay}>
          <div className={styles.loader}>
            <Loading />
          </div>
        </div>
      )}
    </div>
  );
}